import React, { useState } from 'react';
import { todayKST, nowKST, dayOfWeekUTC, daysInMonth } from '../utils/date';

const WEEKDAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

function parseYmdParts(value) {
  const text = String(value || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return null;
  const [year, month, day] = text.split('-').map(Number);
  return { year, month, day };
}

function toYmd(year, month, day) {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export default function SingleDatePicker({ value, onChange }) {
  const today = todayKST();
  const initial = parseYmdParts(value) || {
    year: nowKST().getUTCFullYear(),
    month: nowKST().getUTCMonth() + 1,
  };
  const [viewYear, setViewYear] = useState(initial.year);
  const [viewMonth, setViewMonth] = useState(initial.month);

  const firstDow = dayOfWeekUTC(toYmd(viewYear, viewMonth, 1));
  const totalDays = daysInMonth(viewYear, viewMonth);
  const cells = [];
  for (let i = 0; i < firstDow; i += 1) cells.push(null);
  for (let day = 1; day <= totalDays; day += 1) cells.push(day);

  function moveMonth(delta) {
    let nextYear = viewYear;
    let nextMonth = viewMonth + delta;
    if (nextMonth < 1) {
      nextYear -= 1;
      nextMonth = 12;
    } else if (nextMonth > 12) {
      nextYear += 1;
      nextMonth = 1;
    }
    setViewYear(nextYear);
    setViewMonth(nextMonth);
  }

  return (
    <div style={wrapStyle}>
      <div style={navStyle}>
        <button type="button" style={navBtnStyle} onClick={() => moveMonth(-1)}>‹</button>
        <strong style={monthStyle}>{viewYear}년 {viewMonth}월</strong>
        <button type="button" style={navBtnStyle} onClick={() => moveMonth(1)}>›</button>
      </div>
      <div style={gridStyle}>
        {WEEKDAY_LABELS.map((label, index) => (
          <div key={label} style={weekdayStyle(index)}>{label}</div>
        ))}
        {cells.map((day, index) => {
          if (!day) return <div key={`empty-${index}`} />;
          const ymd = toYmd(viewYear, viewMonth, day);
          const selected = value === ymd;
          const isToday = ymd === today;
          const dow = (firstDow + day - 1) % 7;
          return (
            <button
              key={ymd}
              type="button"
              style={dayStyle(selected, isToday, dow)}
              onClick={() => onChange(ymd)}
              title={ymd}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const wrapStyle = {
  border: '1px solid var(--border)',
  borderRadius: 8,
  padding: 10,
  background: 'var(--bg-card)',
};

const navStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 8,
};

const navBtnStyle = {
  border: '1px solid var(--border)',
  borderRadius: 6,
  background: 'var(--bg-subtle)',
  color: 'var(--text)',
  width: 32,
  height: 28,
  cursor: 'pointer',
  fontSize: 18,
};

const monthStyle = {
  color: 'var(--text)',
  fontSize: 13,
};

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
  gap: 3,
};

const weekdayStyle = (index) => ({
  textAlign: 'center',
  fontSize: 11,
  fontWeight: 700,
  color: index === 0 ? '#ef4444' : index === 6 ? '#2563eb' : 'var(--text-muted)',
  padding: '2px 0',
});

const dayStyle = (selected, isToday, dow) => ({
  border: isToday && !selected ? '1px solid #2563eb' : '1px solid transparent',
  borderRadius: 6,
  minHeight: 30,
  cursor: 'pointer',
  background: selected ? '#2563eb' : 'var(--bg-subtle)',
  color: selected ? '#fff' : dow === 0 ? '#dc2626' : dow === 6 ? '#2563eb' : 'var(--text)',
  fontWeight: selected || isToday ? 800 : 500,
});
